import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import TESTAREA_ICON from '../assets/icons/ComniqaiIcon-V3.0/testing-area-icon.svg';
import { MdKeyboardArrowRight } from 'react-icons/md';
import THUNDERBOLT_ICON from '../assets/icons/ComniqaiIcon-V3.0/conversations-blueicon.svg';
import { deleteWithToken, getLocalStorage, getWithoutToken, getWithToken, patchWithToken, postWithToken } from '../API/Api';
import { toast } from 'react-toastify';
import Linkify from 'react-linkify';
import { formatText, getPlanLevel, PLAN_LEVELS } from '../API/Constant';
import ThreeDotLoader from '../components/ThreeDotLoader';
import ScrollToBottom from '../components/ScrollToBottom';
import CalendlyEmbed from '../components/CalendlyEmbed';
import { FaSave } from 'react-icons/fa';
import TextareaAutosize from 'react-textarea-autosize';
import { BiEdit, BiTrash } from 'react-icons/bi';
import AIPreview from '../components/AIPreview';
import { useLocation, useNavigationType } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SubContext from '../contexts/SubContext';

const initialFormValue = {
  name: '',
  email: '',
  phone_number: '',
};

const AIChatTestLab = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [activeScenario, setActiveScenario] = useState('general');
  const [welcomeDetails, setWelcomeDetails] = useState([]);
  const [chatData, setChatData] = useState([]);
  const [apiTokenData, setApiTokenData] = useState(null);
  const [values, setValues] = useState('');
  const [loader, setLoader] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [hideButtons, setHideButtons] = useState(false);
  const [ipAddress, setIpAddress] = useState('');
  const [showRecommendation, setShowRecommendation] = useState(null);
  const [showFormButton, setShowFormButton] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isLeadButtonHide, setIsLeadButtonHide] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirmPopup, setDeleteConfirmPopup] = useState(false);
  const [showCalendar, setShowCalendar] = useState({
    show: false,
    link: '',
  });
  const [formData, setFormData] = useState(initialFormValue);
  const { name, email, phone_number } = formData;

  const [editIndex, setEditIndex] = useState('');
  const [editValue, setEditValue] = useState('');
  const [openChatbox, setOpenChatbox] = useState(false);
  const [configData, setConfigData] = useState({
    model_name: '',
    test_model_name: '',
    api_key: '',
  });

  const subsFlag = useContext(SubContext);

  const subscriptionInfo = subsFlag?.subscriptionFlag?.user?.subscription ?? null;
  const subscriptionName = subscriptionInfo?.name ?? 'Free';
  const planLevel = useMemo(() => getPlanLevel(subscriptionName), [subscriptionName]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetch('https://api.ipify.org/?format=json')
      .then((res) => res.json())
      .then((data) => setIpAddress(data?.ip))
      .catch((err) => console.error('Error fetching IP:', err));
  }, []);

  useEffect(() => {
    const isInitialNavigation = navigationType === 'PUSH' && location.state?.autostart;
    if (isInitialNavigation) {
      setOpenChatbox(true);
    }
  }, [location, navigationType, setOpenChatbox]);

  useEffect(() => {
    const fetchData = async () => {
      const token = getLocalStorage('apiToken');
      if (token) {
        const parsedToken = JSON.parse(token);
        setApiTokenData(parsedToken);
        await fetchBotScenarioDetails(activeScenario, parsedToken);
        fetchConfigurationDetails(parsedToken);
      }
    };

    fetchData();
  }, [activeScenario]);

  const fetchConfigurationDetails = async (token) => {
    try {
      const response = await getWithToken('openai_configurations', token);
      if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
        setConfigData(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch configuration.');
    }
  };

  useEffect(() => {
    setSessionId(generateRandomSessionId());
  }, []);

  const generateRandomSessionId = () => {
    const numbers = '0123456789';
    const length = 8;
    let randomId = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * numbers.length);
      randomId += numbers.charAt(randomIndex);
    }
    return randomId;
  };

  const handleScenarioClick = (scenario) => {
    setActiveScenario(scenario);
    handleClearData();
  };

  const fetchBotScenarioDetails = async (scenario, token) => {
    try {
      const response = await getWithToken(`test_conversion?conversation_type=${scenario}`, token);
      if (response.status === 200) {
        const data = response.data;
        setWelcomeDetails(data);
      }
    } catch (error) {
      console.error('Error fetching bot details:', error);
      toast.error('Something went wrong');
    }
  };

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (values.trim().length > 0) {
      let newObj = { prmt: values };
      chatData.push(newObj);
      let body = {
        prompt: values,
        session_id: sessionId,
      };
      setLoader(true);
      setShowRecommendation(null);
      setIsButtonDisabled(true);
      setValues('');
      setHideButtons(true);
      chatPostApi(body, activeScenario);
    } else {
      toast.dismiss();
      toast.error('Message is blank.');
      setLoader(false);
    }
  };

  const handleChoice = (e, Data) => {
    setHideButtons(true);
    setIsButtonDisabled(true);
    let newObj = { prmt: Data };
    chatData.push(newObj);
    let body = {
      prompt: Data,
      session_id: sessionId,
      ip_address: ipAddress,
    };
    setLoader(true);
    chatPostApi(body, activeScenario);
  };

  const chatPostApi = async (body, activeScenario) => {
    try {
      const response = await postWithToken(`test_conversion?conversation_type=${activeScenario}`, apiTokenData, body);

      if (response.status === 200) {
        const tempChatData = [...chatData];
        const { data } = response;
        data.forEach((element) => {
          let tempData = {
            choice: element,
            text: element.text,
            id: element.id,
          };
          if (!welcomeDetails?.lead_choice) setHideButtons(false);
          if (element?.recommendation) getShowRecommendation(element?.recommendation);
          if (element?.lead_form) setShowForm(true);
          if (element?.calendar)
            setShowCalendar({
              show: true,
              link: element?.calendar,
            });
          tempChatData.push(tempData);
        });

        setChatData(tempChatData);
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error('Error in chatPostApi:', error);
      toast.error('An unexpected error occurred.');
    } finally {
      setIsButtonDisabled(false);
      setLoader(false);
    }
  };

  const getShowRecommendation = (id) => {
    getWithoutToken(`test_lead_answers_retrieve/${id}`)
      .then((response) => {
        if (response.status === 200) {
          setShowRecommendation(response?.data);
        }
      })
      .catch((error) => {
        console.error('error: ', error);
        toast.error('Something went wrong');
      });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const emailPattern = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;

    if (!emailPattern.test(formData.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    const formattedPrompt = `**Name:** ${formData.name}\n**Email:** ${formData.email}\n**Number:** ${formData.phone_number}`;
    handleChoice(e, formattedPrompt);
    setShowForm(false);
    setFormData(initialFormValue);
  };

  const handleClearData = () => {
    deleteWithToken('ChatGPTAPI_', apiTokenData)
      .then((response) => {
        if (response.status === 200) {
          setChatData([]);
          setSessionId(generateRandomSessionId());
          setShowFormButton(false);
          setHideButtons(false);
          setShowRecommendation(null);
          setIsLeadButtonHide(false);
          setIsButtonDisabled(false);
          setDeleteConfirmPopup(false);
          setShowCalendar({});
          setChatData([]);
          setShowForm(false);
        }
      })
      .catch((error) => {
        console.error('error: ', error);
        toast.error('Something went wrong');
      });
  };
  const fetchCalendlyLink = async () => {
    try {
      const companyIdToUse = welcomeDetails?.company_id;
      const url = `create_calendly_scheduling_link_withouttoken/?company=${companyIdToUse}`;
      const response = await postWithToken(url, apiTokenData, { session_id: sessionId });
      if (response?.status === 200) {
        return response.data;
      } else if (response?.status === 400) {
        toast.error(response?.message);
        return null;
      }
    } catch (error) {
      console.error('Error fetching Calendly link:', error);
      toast.error('Something went wrong');
      return null;
    }
  };

  const handelEditChatData = (newData, index) => {
    setEditIndex(newData.id);
    setEditValue(newData.text);
  };

  const handelSaveChatData = (newData) => {
    let obj = {
      text: editValue,
    };
    if (editValue !== '') {
      patchWithToken('Chat_logAPI/' + newData.id, apiTokenData, obj)
        .then((response) => {
          if (response.status === 200) {
            toast.success(response?.message);
            setEditIndex('');
            const tempArray = [...chatData];
            tempArray.forEach((element, index) => {
              if (element.id === newData.id) {
                tempArray[index].text = editValue;
              }
            });
            setLoader(false);
          }
        })
        .catch((error) => {
          // toast.error('Something went wrong');
        });
    } else {
      toast.dismiss();
      toast.error('Empty text field');
    }
  };

  const handleSelect = (value) => {
    const updatedData = { ...configData, test_model_name: value };
    setConfigData(updatedData);
    saveConfigOnChange(updatedData);
    setDropdownOpen(false);
  };

  const saveConfigOnChange = async (updatedValues) => {
    if (!updatedValues.test_model_name) {
      return;
    }

    const payload = {
      test_model_name: updatedValues.test_model_name,
    };

    try {
      let response;

      if (configData?.id) {
        response = await patchWithToken(`openai_configurations/${configData.id}`, apiTokenData, payload);
      } else {
        response = await postWithToken('openai_configurations', apiTokenData, payload);
      }

      const { status, message, code } = response;

      if (status === 200 || status === 201) {
        toast.success(message || 'Configuration saved.');
        fetchConfigurationDetails(apiTokenData);
      } else {
        toast.error(message || 'Something went wrong.');
      }
    } catch (error) {
      toast.error('Error while saving configuration.');
    }
  };

  const modelOptions = [
    { value: 'gpt-3.5-turbo-16k', label: 'gpt-3.5-turbo-16k' },
    { value: 'gpt-4o-mini', label: 'gpt-4o-mini' },
    ...(planLevel >= PLAN_LEVELS.Growth
      ? [
          { value: 'gpt-4o', label: 'gpt-4o' },
          { value: 'gpt-5', label: 'gpt-5' },
          { value: 'gpt-5-mini', label: 'gpt-5-mini' },
        ]
      : []),
  ];

  return (
    <div className='comniq-container'>
      <div class='ai-lab-header d-flex flex-wrap justify-content-between align-items-center gap-2'>
        <div>
          <h6 class='mb-0 outfit-bold d-flex align-items-center'>
            <img src={TESTAREA_ICON} alt='testing-icon' class='me-2' /> AI Testing Laboratory
          </h6>
          <p class='outfit-regular mb-0'>Test your chatbot and refine responses before publishing</p>
        </div>
      </div>
      <div className='row mt-4 d-flex flex-wrap align-items-stretch' style={{ minHeight: '100%' }}>
        <div className='col-md-4 col-sm-12 col-12 d-flex'>
          <div className='comniq-container test-scenarios-section w-100 h-100'>
            <div className='d-flex justify-content-between align-items-center gap-2 mb-3'>
              <h6 className='test-scenarios__title outfit-medium mb-0'>Test Scenarios</h6>

              <div className='select-custom-dropdown-container' ref={dropdownRef} style={{ width: '170px' }}>
                <div className='select-custom-dropdown-selected' onClick={() => setDropdownOpen((prev) => !prev)}>
                  {configData.test_model_name || 'Choose the model'}
                  <span className='select-custom-dropdown-arrow'>{dropdownOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
                </div>

                {dropdownOpen && (
                  <ul className='select-custom-dropdown-options'>
                    {modelOptions.map((option) => (
                      <li key={option.value} className='select-custom-dropdown-option' onClick={() => handleSelect(option.value)}>
                        {option.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className='test-scenarios__group d-flex flex-column justify-content-between align-items-start gap-2'>
              <button
                className={`test-scenarios__button outfit-medium ${activeScenario === 'general' ? 'test-scenarios__button--active' : ''}`}
                onClick={() => handleScenarioClick('general')}
              >
                General Conversation {activeScenario === 'general' && <MdKeyboardArrowRight />}
              </button>
              <button
                className={`test-scenarios__button outfit-medium ${activeScenario === 'faq' ? 'test-scenarios__button--active' : ''}`}
                onClick={() => handleScenarioClick('faq')}
              >
                FAQ Testing {activeScenario === 'faq' && <MdKeyboardArrowRight />}
              </button>
              <button
                className={`test-scenarios__button outfit-medium ${activeScenario === 'leap' ? 'test-scenarios__button--active' : ''}`}
                onClick={() => handleScenarioClick('leap')}
              >
                L.E.A.P Testing {activeScenario === 'leap' && <MdKeyboardArrowRight />}
              </button>
            </div>
            <button className='test-scenarios__preview-btn outfit-medium w-100 my-3' onClick={() => setOpenChatbox(!openChatbox)}>
              {!openChatbox ? 'Show Live Preview' : 'Hide Test Bot'}
            </button>
            <div className='test-scenarios__tips'>
              <strong className='outfit-medium'>Testing Tips:</strong>
              <ul className='test-scenarios__tips-list mt-2'>
                <li>Click "Edit" on any bot message to refine responses</li>
                <li>Test different user scenarios to ensure comprehensive coverage</li>
                <li>Save your best responses to the knowledge base</li>
              </ul>
            </div>
          </div>
        </div>

        <div className='col-md-8 col-sm-12 col-12 d-flex'>
          <div className='chat-panel d-flex flex-column w-100 h-100'>
            {/* Header */}
            <div className='chat-panel__header d-flex justify-content-between align-items-center'>
              <div>
                <h6 className='chat-panel__title outfit-medium'>Test Conversation</h6>
                <p className='chat-panel__subtitle outfit-regular'>Edit and refine your bot responses</p>
              </div>
              <span className='chat-panel__badge badge rounded-pill'>{activeScenario === 'general' ? 'General Test' : activeScenario === 'faq' ? 'FAQ Test' : 'L.E.A.P Test'}</span>
            </div>

            {/* Scrollable chat box */}
            <div className='chat-panel__messages flex-grow-1 overflow-auto' style={{ maxHeight: '400px' }}>
              {welcomeDetails?.welcome_msg && (
                <div className='chat-panel__message mb-3'>
                  <div className='chat-panel__text chat-pannel-right d-inline-block outfit-regular'>{welcomeDetails?.welcome_msg}</div>
                </div>
              )}
              {chatData.length > 0 &&
                chatData?.map((obj, index) => {
                  return (
                    <>
                      <div className='chat-panel__message' key={index}>
                        {obj.prmt && (
                          <div className='mb-3 text-end'>
                            <div className='chat-panel__text chat-pannel-left d-inline-block outfit-regular'>{formatText(obj?.prmt)}</div>
                          </div>
                        )}

                        {obj?.text && (
                          <div className='d-flex align-items-center mb-3'>
                            {editIndex !== obj.id ? (
                              <div className='chat-panel__text chat-pannel-right d-inline-block outfit-regular position-relative' style={{ whiteSpace: 'pre-line' }}>
                                <Linkify
                                  componentDecorator={(decoratedHref, decoratedText, key) => (
                                    <a target='blank' href={decoratedHref} key={key}>
                                      {decoratedText}
                                    </a>
                                  )}
                                >
                                  {formatText(obj?.text)}
                                </Linkify>
                                <div className='editchat-box-icon edit-icon-position'>
                                  <button className='d-flex justify-content-center align-items-center' type='button' onClick={() => handelEditChatData(obj, index)}>
                                    <BiEdit /> <span className='outfit-medium ms-1'> Edit</span>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className='chat-edit-box d-flex flex-column align-items-center'>
                                <TextareaAutosize
                                  className='form-control chatbox-input box-shadow-hover py-2'
                                  placeholder='Enter category here...'
                                  onChange={(e) => setEditValue(e.target.value)}
                                >
                                  {obj.text}
                                </TextareaAutosize>
                                <div className='d-flex justify-content-end gap-2 mt-2 w-100'>
                                  <button className='btn-modal-cancel outfit-medium' onClick={() => setEditIndex(null)}>
                                    Cancel
                                  </button>
                                  <button className='btn-modal-save outfit-medium' onClick={() => handelSaveChatData(obj, index)}>
                                    <FaSave className='me-1' />
                                    Save
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })}

              {!hideButtons && welcomeDetails?.lead_choice != null && (
                <div className='d-flex align-items-center mt-2 w-100 gap-2'>
                  {welcomeDetails?.lead_choice.map((choice, index) => (
                    <React.Fragment key={index}>
                      <button
                        className='btn btn-submit-login outfit-regular font14 h-auto bg-theme-cerisePink'
                        onClick={
                          index === 0
                            ? (e) => handleChoice(e, choice)
                            : (e) => {
                                handleChoice(e, choice);
                                setShowFormButton(true);
                              }
                        }
                      >
                        {choice}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              )}

              {showRecommendation && (
                <div className='chat-group d-flex flex-wrap align-items-center gap-2 mt-0'>
                  {showRecommendation?.map((recommendation, index) => (
                    <button
                      key={index}
                      className='btn preview-chat-group-btn outfit-regular font14 w-auto h-auto border-0'
                      style={{
                        borderRadius: '12px',
                        minHeight: 'unset',
                        lineHeight: '20px',
                        padding: '10px 25px',
                        whiteSpace: 'nowrap',
                      }}
                      onClick={(e) => {
                        handleChoice(e, recommendation?.answer);
                        setShowRecommendation(null);
                      }}
                    >
                      {recommendation?.answer}
                    </button>
                  ))}
                </div>
              )}
              {loader && (
                <div className='custom-chat-anstext d-inline-block mb-3' style={{ width: 'fit-content' }}>
                  <div className='d-flex align-items-center'>
                    <ThreeDotLoader />
                  </div>
                </div>
              )}

              {showForm && (
                <div className='d-flex justify-content-center align-items-center w-100'>
                  <div className='user-box-wrapper position-relative' style={{ width: '40%', maxWidth: '100%' }}>
                    <div className='user-box'>
                      <div className='user-box-img p-0'>
                        <div
                          className='widget-user-icon'
                          style={{
                            width: '40px',
                            height: '40px',
                            minWidth: '40px',
                            minHeight: '40px',
                          }}
                        >
                          {/* <img src={widgetDetails?.chatbot_avtar || SIDEBAR_BOX_IMG} alt='chatbot-icon' /> */}
                        </div>
                      </div>
                      <form id='form' onSubmit={handleFormSubmit}>
                        <div className='mb-3'>
                          <label className='outfit-medium'>Name</label>
                          <input
                            type='text'
                            className='form-control'
                            name='name'
                            id='name'
                            required
                            autoComplete='off'
                            placeholder='Enter your name'
                            value={name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          />
                        </div>
                        <div className='mb-3'>
                          <label className='outfit-medium'>Email</label>
                          <input
                            type='email'
                            className='form-control'
                            name='email'
                            required
                            autoComplete='off'
                            placeholder='Enter your email'
                            value={email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          />
                        </div>
                        <div className='mb-4'>
                          <label className='outfit-medium'>Phone</label>
                          <input
                            type='number'
                            className='form-control'
                            name='phone_number'
                            required
                            autoComplete='off'
                            placeholder='Enter your number'
                            maxLength='10'
                            value={phone_number}
                            onChange={(e) => {
                              if (e.target.value.length <= 10) {
                                setFormData({ ...formData, phone_number: e.target.value });
                              }
                            }}
                          />
                        </div>

                        <input
                          id='submit'
                          type='submit'
                          disabled={name === '' || phone_number === '' || email === '' || isButtonDisabled}
                          style={{
                            opacity: name === '' || phone_number === '' || email === '' ? 0.3 : 1,
                            cursor: name === '' || phone_number === '' || email === '' ? 'not-allowed' : 'pointer',
                          }}
                        />
                      </form>
                    </div>
                  </div>
                </div>
              )}
              {showCalendar.show && <CalendlyEmbed url={showCalendar.link} />}
              <ScrollToBottom scrollChatData={chatData} scrollLoader={loader} showForm={showForm} showRecommendation={showRecommendation} />
            </div>
            <div className='p-2'>
              {hideButtons || !welcomeDetails?.lead_status ? (
                <>
                  {showFormButton && !isLeadButtonHide && (
                    <div className='d-flex align-items-center justify-content-center pb-2 gap-2'>
                      {/* <button
                        className='btn btn-submit-login outfit-regular font14 h-auto bg-theme-cerisePink'
                        onClick={(e) => {
                          handleChoice(e, welcomeDetails?.create_lead_choice);
                          setIsLeadButtonHide(true);
                        }}
                        disabled={isButtonDisabled}
                      >
                        {welcomeDetails?.create_lead_choice}
                      </button> */}

                      <button
                        className='btn btn-submit-login outfit-regular font14 h-auto bg-theme-cerisePink'
                        onClick={async () => {
                          setIsLeadButtonHide(true);
                          if (welcomeDetails?.lead_form) {
                            setShowForm(true);
                          } else {
                            const calendlyLink = await fetchCalendlyLink();
                            if (calendlyLink) {
                              setShowCalendar({ show: true, link: calendlyLink });
                            }
                          }
                        }}
                        disabled={isButtonDisabled}
                      >
                        {welcomeDetails?.generate_lead_choice}
                      </button>
                    </div>
                  )}
                </>
              ) : null}
            </div>
            {/* <form className='chat-panel__input-form d-flex align-items-center w-100'>
              <div className='chat-panel__input w-100 d-flex align-items-center gap-2'>
                <input
                  type='text'
                  className='form-control outfit-regular'
                  placeholder='Type a test message...'
                  value={values}
                  onChange={(e) => setValues(e.target.value)}
                  disabled={isButtonDisabled || showForm || showCalendar.show}
                />
                <button className='btn' onClick={handleChatSubmit} disabled={isButtonDisabled || showForm || showCalendar.show}>
                  <MdKeyboardArrowRight />
                </button>
              </div>
            </form> */}

            <form className='chat-panel__input-form d-flex align-items-center w-100'>
              <div className='chat-panel__input w-100 d-flex align-items-center gap-2'>
                {/* Delete Icon Button */}
                <button type='button' className='btn delete-btn d-flex align-items-center justify-content-center' onClick={() => setDeleteConfirmPopup(true)}>
                  <BiTrash />
                </button>

                <input
                  type='text'
                  className='form-control outfit-regular'
                  placeholder='Type a test message...'
                  value={values}
                  onChange={(e) => setValues(e.target.value)}
                  disabled={showForm || showCalendar.show}
                />
                <button className='btn' onClick={handleChatSubmit} disabled={isButtonDisabled || showForm || showCalendar.show}>
                  <MdKeyboardArrowRight />
                </button>
              </div>
            </form>

            {deleteConfirmPopup && (
              <div className='custom-popup'>
                <div className='popup-content'>
                  <hr />
                  <div className={'popup-head'}>
                    <h4 className='font18  outfit-regular'> Are you sure you want to permanently delete and reset the chat? </h4>
                  </div>
                  <hr />
                  <div className='popup-footer d-flex align-items-center gap-3'>
                    <button className='btn-modal-save outfit-medium' onClick={() => handleClearData()}>
                      Yes
                    </button>
                    <button className='btn-modal-cancel outfit-medium' data-dismiss='modal' onClick={() => setDeleteConfirmPopup(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <AIPreview openChatbox={openChatbox} setOpenChatbox={setOpenChatbox} />
      </div>
    </div>
  );
};

export default AIChatTestLab;
